import { useState, useCallback } from 'react';
import svgPaths from '../imports/svg-l7usstgz1l';
import { AnimatedGiraffe } from './components/AnimatedGiraffe';
import { ModeSwitch } from './components/ModeSwitch';

function LogoTotem() {
  return (
    <div className="relative shrink-0 size-[48px]">
      <div className="flex flex-row items-center justify-center size-full">
        <div className="content-stretch flex items-center justify-center pr-[48px] relative size-full">
          <div className="flex-[1_0_0] h-full min-h-px min-w-px mr-[-48px] relative">
            <svg
              className="absolute block size-full"
              fill="none"
              preserveAspectRatio="none"
              viewBox="0 0 48 48"
            >
              <g>
                <path d={svgPaths.p3e8e7280} fill="currentColor" />
                <path d={svgPaths.p6e4c300} fill="currentColor" />
                <path d={svgPaths.p255a0c00} fill="currentColor" />
              </g>
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}

function DocsIcon({ color }: { color: string }) {
  return (
    <div className="relative shrink-0 size-[32px]">
      <div className="absolute inset-[12.5%_0_6.25%_0]">
        <svg
          className="absolute block size-full"
          fill="none"
          preserveAspectRatio="none"
          viewBox="0 0 16 13"
        >
          <g>
            <mask fill="white" id="docs-mask">
              <path d="M11.2 1H0V0H11.2V1Z" />
              <path d={svgPaths.p3162af80} />
              <path d="M0 11.4H7.2V10.4H0V11.4Z" />
              <path d={svgPaths.p1269ff0} />
              <path d={svgPaths.p38e82680} />
              <path d={svgPaths.p2cc1da80} />
            </mask>
            <path d={svgPaths.p143e0900} fill={color} mask="url(#docs-mask)" />
          </g>
        </svg>
      </div>
    </div>
  );
}

export default function App() {
  const [mode, setMode] = useState<'lucent' | 'indigo'>('lucent');
  const isDark = mode === 'indigo';

  const bgColor = isDark ? '#272145' : '#fefeff';
  const textColor = isDark ? '#c0b6ed' : '#0e0d46';
  const mutedColor = isDark ? '#8078a8' : '#505053';
  const logoColor = isDark ? '#c0b6ed' : '#2d178b';
  const docsColor = isDark ? '#c0b6ed' : '#0e0d46';

  const handleModeChange = useCallback((m: 'lucent' | 'indigo') => {
    setMode(m);
  }, []);

  return (
    <div
      className="min-h-screen relative w-full overflow-hidden font-['Urbanist',sans-serif] transition-colors duration-500"
      style={{ backgroundColor: bgColor }}
    >
      {/* Header */}
      <header className="h-[100px] max-md:h-[72px] w-full relative z-20">
        <div className="flex items-center size-full px-10 max-md:px-4">
          {/* Logo */}
          <div className="flex items-center gap-[2px] shrink-0" style={{ color: logoColor }}>
            <div
              className="italic text-[24px] whitespace-nowrap max-md:hidden"
              style={{ fontWeight: 700, fontFamily: "'Urbanist', sans-serif" }}
            >
              <p className="leading-none">Daraa</p>
            </div>
            <LogoTotem />
          </div>

          <div className="flex-1" />

          {/* Nav actions */}
          <nav className="flex gap-6 max-md:gap-3 items-center">
            <ModeSwitch
              className="relative shrink-0 size-[64px] max-md:size-[48px]"
              onChange={handleModeChange}
            />
            <a
              href="https://neighborhood.daraa.io/login"
              className="text-[18px] max-md:text-[14px] underline decoration-solid transition-colors duration-500 hover:opacity-70"
              style={{ fontWeight: 500, color: mutedColor }}
            >
              Console
            </a>
            <a
              href="https://form.typeform.com/to/gPZVQbCv?typeform-source=www.daraa.io"
              target="_blank"
              rel="noopener noreferrer"
              className="h-[48px] max-md:h-[36px] relative rounded-[20px] max-md:rounded-[14px] shrink-0 w-[124px] max-md:w-[96px] cursor-pointer group active:scale-95 transition-transform flex items-center justify-center"
            >
              <div className="flex items-center justify-center overflow-clip rounded-[inherit] size-full relative">
                <div
                  className="absolute inset-0 bg-gradient-to-b transition-all duration-500 group-hover:brightness-110"
                  style={{
                    backgroundImage: isDark
                      ? 'linear-gradient(to bottom, #332b5e, #231e42)'
                      : 'linear-gradient(to bottom, #fafafa, #eae9ec)',
                  }}
                />
                <div
                  className="absolute inset-0 rounded-[inherit] border-[0.5px] border-solid transition-colors duration-500"
                  style={{ borderColor: isDark ? '#576889' : '#576889' }}
                />
                <span
                  className="relative text-[18px] max-md:text-[14px] whitespace-nowrap transition-colors duration-500"
                  style={{ fontWeight: 600, color: isDark ? '#fefeff' : '#b6bece' }}
                >
                  {`Let's Talk`}
                </span>
              </div>
            </a>
            <a
              href="https://docs.daraa.io/welcome/introduction"
              className="flex flex-col items-center gap-[2px] shrink-0 hover:opacity-70 transition-opacity"
            >
              <DocsIcon color={docsColor} />
              <span
                className="text-[10px] underline decoration-solid transition-colors duration-500"
                style={{ fontWeight: 500, color: textColor }}
              >
                Docs
              </span>
            </a>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main
        className="flex-1 w-full px-10 max-md:px-4 relative max-md:overflow-y-auto max-md:h-auto"
        style={{ height: 'calc(100vh - 108px)' }}
      >
        <div className="grid grid-cols-2 size-full items-stretch gap-[10px] max-md:grid-cols-1 max-md:h-auto max-md:gap-4 max-md:flex max-md:flex-col max-md:min-h-full">
          {/* Left Column - Text (container for cqw scaling) */}
          <div
            className="flex flex-col justify-center h-full pt-[96px] max-lg:pt-10 max-md:pt-4 max-md:items-center max-md:text-center max-md:h-auto z-10"
            style={{ containerType: 'inline-size' }}
          >
            {/* Headline - 3 lines, scales with container width */}
            <div
              className="flex flex-col gap-[8px] max-md:gap-[4px] w-full"
              style={{ color: textColor }}
            >
              <p
                className="leading-[1.25]"
                style={{ fontWeight: 800, fontSize: 'clamp(22px, 9.07cqw, 64px)' }}
              >
                Own Your Brand.
              </p>
              <p
                className="leading-[1.25]"
                style={{ fontWeight: 800, fontSize: 'clamp(22px, 9.07cqw, 64px)' }}
              >
                Own Your Operations.
              </p>
              <p
                className="leading-[1.25]"
                style={{ fontWeight: 800, fontSize: 'clamp(22px, 9.07cqw, 64px)' }}
              >
                Take Back Your Smiles.
              </p>
            </div>

            {/* Subtext - scales proportionally, same width as headlines */}
            <div className="py-[16px] max-md:py-[10px] w-full overflow-clip">
              <p
                className="leading-[1.25] transition-colors duration-500"
                style={{
                  fontWeight: 500,
                  color: textColor,
                  fontSize: 'clamp(13px, 4.53cqw, 32px)',
                }}
              >
                <span>
                  AI-driven commerce operations. One solution to unify your stack, erase
                  fragmentation, drive conversion, and{' '}
                </span>
                <span className="italic">deliver something more fulfilling...</span>
              </p>
            </div>

            {/* Spacer */}
            <div className="flex-1 max-md:hidden" />

            {/* Copyright */}
            <div className="py-[16px] max-md:hidden">
              <p
                className="italic leading-[1.25] transition-colors duration-500"
                style={{ fontWeight: 500, color: textColor, fontSize: 'clamp(10px, 1.7cqw, 12px)' }}
              >
                &copy; 2026 Daraa, Inc. All rights reserved.
              </p>
            </div>
          </div>

          {/* Right Column - Animated Giraffe (container for scaling) */}
          <div
            className="relative h-full flex items-center justify-center overflow-clip max-lg:absolute max-lg:right-[-10%] max-lg:top-0 max-lg:h-full max-lg:w-[60%] max-lg:opacity-15 max-md:static max-md:w-full max-md:h-[50vw] max-md:opacity-100 max-md:shrink-0"
            style={{ containerType: 'inline-size' }}
          >
            <AnimatedGiraffe className="w-full h-full" color={textColor} />
          </div>

          {/* Mobile Copyright - shown below giraffe */}
          <div className="hidden max-md:block max-md:flex-1" />
          <div className="hidden max-md:block py-4 pb-8 text-center">
            <p
              className="italic leading-[1.25] transition-colors duration-500"
              style={{ fontWeight: 500, color: textColor, fontSize: '12px' }}
            >
              &copy; 2026 Daraa, Inc. All rights reserved.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
